import React, { useMemo } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import useUrlQuery from '~/hooks/useQuery';

import CharterSeatLegend from './components/CharterSeatLegend';
import CharterSeatMap from './components/CharterSeatMap';
import CharterSeatSearch from './components/CharterSeatSearch';
import PaymentCaptureCard from './components/PaymentCaptureCard';
import TravellerSeatTable from './components/TravellerSeatTable';
import { useCharterSeatManagement } from './hooks/useCharterSeatManagement';

export default function CharterSeatsPage(): React.JSX.Element {
  const query = useUrlQuery();
  const initialPnr = query.get('charterPnr') ?? undefined;
  const {
    pnr,
    setPnr,
    charterDetails,
    seatSelections,
    isSearching,
    searchError,
    seatPaymentCollected,
    setSeatPaymentCollected,
    seatPaymentReference,
    setSeatPaymentReference,
    isSaving,
    saveError,
    saveConfirmation,
    handleSearch,
    handleSeatChange,
    availableSeatOptions,
    handleSave,
    seatPriceForSelection,
    seatLegendItems,
    selectedSeatsPrices,
    getSelectionError,
    hasInvalidSelections,
    reservationSyncBlocksSave,
    getTravellerCabin,
    handleCabinChange,
    cabinOptionsForFlight,
    hasCabinChanges,
  } = useCharterSeatManagement({ initialPnr });

  const areAllSelectedSeatsFree = useMemo(
    () => selectedSeatsPrices.length > 0 && selectedSeatsPrices.every((price) => price === 0),
    [selectedSeatsPrices],
  );

  return (
    <Stack spacing={4}>
      <Typography variant="h4" fontWeight={600}>
        Charter seats
      </Typography>

      <Box>
        <CharterSeatSearch pnr={pnr} onPnrChange={setPnr} onSearch={handleSearch} isLoading={isSearching} />
      </Box>

      {searchError ? <Alert severity="error">{searchError}</Alert> : null}
      {isSearching ? <LinearProgress sx={{ maxWidth: 320 }} /> : null}

      {charterDetails ? (
        <Stack spacing={4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Stack>
              <Typography variant="h5" gutterBottom>
                Travellers Info
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please add or change seats via the dropdowns below. To remove an assigned seat, cancel the seat add-on
                from the order (refund flow)—clearing a seat here is not available.
              </Typography>
            </Stack>

            <Accordion sx={{ maxWidth: 900 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  ℹ️ Seat Availability Rules
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'background.paper' }}>
                <Stack component="ul" spacing={0.5} sx={{ mt: 0, mb: 0, pl: 2 }}>
                  <Typography component="li" variant="body2">
                    <strong>Infants (INF):</strong> Do not receive a seat assignment as they sit on an adult's lap. Seat
                    selection and pricing will not be available.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>Children on emergency row side seats:</strong> Children cannot sit on side seats of
                    emergency rows (e.g., columns A, B, G, H of row 50), but may sit in middle seats of emergency rows.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>Children require adult companion:</strong> If a child sits on an emergency row side seat, an
                    adult must be seated immediately next to them. Two children cannot sit next to each other on
                    emergency row side seats.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>Adults with infants in booking:</strong> If any infant is in the PNR, adults cannot sit on
                    emergency row side seats. However, adults can select first row seats with bassinet.
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <TravellerSeatTable
              travellers={charterDetails.travellers}
              flights={charterDetails.flights}
              seatSelections={seatSelections}
              onSeatChange={handleSeatChange}
              getSeatOptions={(traveller, flight) => availableSeatOptions(traveller, flight, charterDetails.travellers)}
              getSeatPrice={seatPriceForSelection}
              getSelectionError={getSelectionError}
              getTravellerCabin={(traveller, flight) => getTravellerCabin(traveller, flight.id)}
              onCabinChange={handleCabinChange}
              getCabinOptions={cabinOptionsForFlight}
            />
          </Box>

          {hasCabinChanges ? (
            <Alert severity="warning">
              Cabin changes are display-only and do not sync to Amadeus. Action the cabin and seat change separately in
              the GDS (Amadeus).
            </Alert>
          ) : null}

          <PaymentCaptureCard
            seatPaymentCollected={seatPaymentCollected}
            onPaymentCollectedChange={setSeatPaymentCollected}
            seatPaymentReference={seatPaymentReference}
            onPaymentReferenceChange={setSeatPaymentReference}
            onSave={handleSave}
            isSaving={isSaving}
            isSaveDisabled={!charterDetails || hasInvalidSelections || reservationSyncBlocksSave}
            saveError={saveError}
            saveConfirmation={saveConfirmation}
            areAllSelectedSeatsFree={areAllSelectedSeatsFree}
          />

          <Divider />

          <Box>
            <Typography variant="h5" gutterBottom>
              Seat Map
            </Typography>
            <CharterSeatLegend legendItems={seatLegendItems} />
            <CharterSeatMap
              flights={charterDetails.flights}
              seatSelections={seatSelections}
              travellers={charterDetails.travellers}
            />
          </Box>
        </Stack>
      ) : (
        <Typography color="text.secondary">Search for a charter PNR to manage seats.</Typography>
      )}
    </Stack>
  );
}
